// apps/client/src/components/AIChat/TypingIndicator.tsx

import React, { useState, useEffect } from 'react';

// Mortgage-broker themed thinking phrases
const THINKING_PHRASES = [
  'Crunching commission numbers...',
  'Analyzing trail book data...',
  'Calculating upfront splits...',
  'Reviewing settlement patterns...',
  'Processing loan pipeline...',
  'Aggregating broker performance...',
  'Scanning clawback risks...',
  'Evaluating trail decay rates...',
  'Cross-referencing RCTI data...',
  'Mining lender insights...',
  'Forecasting trail revenue...',
  'Parsing commission statements...',
  'Correlating settlement dates...',
  'Examining office performance...',
  'Tabulating monthly aggregates...',
];

/**
 * Animated V logo SVG component — brand blue gradient
 */
function AnimatedVLogo() {
  return (
    <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7" style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.5))' }}>
      <defs>
        <linearGradient id="v-thinking-grad" x1="0" y1="0" x2="32" y2="32">
          <stop offset="0%" stopColor="#93c5fd" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#2563eb" />
        </linearGradient>
      </defs>
      <path d="M4 6 L12 6 L16 22 L20 6 L28 6 L20 28 L12 28 Z" fill="url(#v-thinking-grad)" />
    </svg>
  );
}

/**
 * Animated typing indicator with VALUTTEN V logo
 * Brand blue breathe animation with shimmer progress bar
 */
export function TypingIndicator() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [dots, setDots] = useState('');
  const [fading, setFading] = useState(false);

  // Rotate phrases every 3 seconds with fade transition
  useEffect(() => {
    const phraseInterval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % THINKING_PHRASES.length);
        setFading(false);
      }, 300);
    }, 3000);

    return () => clearInterval(phraseInterval);
  }, []);

  // Animate dots
  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);

    return () => clearInterval(dotInterval);
  }, []);

  return (
    <div className="flex justify-start">
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-2xl p-4 max-w-md shadow-lg border border-gray-600/50">
        <div className="flex items-center gap-3.5">
          {/* Animated V Logo with breathe glow */}
          <div className="relative flex-shrink-0" style={{ width: '2.75rem', height: '2.75rem' }}>
            {/* Outer glow */}
            <div
              className="absolute rounded-xl"
              style={{
                inset: '-6px',
                background: 'radial-gradient(circle, rgba(37,99,235,0.3), transparent 70%)',
                filter: 'blur(10px)',
                animation: 'v-breathe 3s ease-in-out infinite',
              }}
            />
            {/* Breathing ring */}
            <div
              className="absolute rounded-xl"
              style={{
                inset: '-3px',
                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                animation: 'v-breathe 3s ease-in-out infinite',
              }}
            >
              <div
                className="absolute rounded-[10px] bg-slate-800"
                style={{ inset: '2px' }}
              />
            </div>
            {/* Inner container with V */}
            <div
              className="relative z-10 rounded-xl flex items-center justify-center overflow-hidden"
              style={{
                width: '2.75rem',
                height: '2.75rem',
                background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              }}
            >
              <AnimatedVLogo />
              {/* Shimmer sweep */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.04) 55%, transparent 70%)',
                  animation: 'v-sweep 2.5s ease-in-out infinite',
                }}
              />
            </div>
          </div>

          {/* Thinking message */}
          <div className="flex flex-col gap-0.5">
            <span
              className="text-blue-400 text-sm font-medium transition-opacity duration-300"
              style={{ opacity: fading ? 0 : 1 }}
            >
              {THINKING_PHRASES[phraseIndex]}
            </span>
            <span className="text-gray-500 text-xs">
              VALUTTEN AI is thinking{dots}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-[3px] bg-gray-600/50 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: '100%',
              background: 'linear-gradient(90deg, #2563eb, #60a5fa, #93c5fd, #60a5fa, #2563eb)',
              backgroundSize: '200% 100%',
              animation: 'v-shimmer 2s linear infinite',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes v-breathe {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.05); }
        }
        @keyframes v-sweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes v-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
